// Entry point for the user-facing web app.
//
// Boot flow:
//   1. Call GET /api/auth/me (cookie-based session check).
//   2. If 401 or no session → render <AuthPage> (login/register/verify/reset).
//   3. If 200 → store user on window.__cortexUser, load the prototype modules
//      in dependency order, then App.jsx mounts via ReactDOM.createRoot.
//
// Auth contract: HttpOnly cookie `cortex_session` set by the backend.
// The frontend never reads/stores/sends a token manually.

// Kundensicht-Wrapper MUSS vor dem ersten fetch stehen (Ops-Impersonation).
import './lib/kundensicht';
import { captureCustomerUpdateLink, clearCustomerUpdateLink } from './features/autopilot/apCustomerUpdateLink.js';
import * as React from 'react';
import * as ReactDOMNS from 'react-dom';
import { createRoot } from 'react-dom/client';
import './styles/index.css';
import type { CreditPackSize, SubscriptionTier } from '@cortex/schemas';
import { DEFAULT_LANG, type Lang, LangStore, isSupportedLang } from '@cortex/i18n';
import { clearPrismCheckoutState } from './features/prism/funnelPersistence';
import {
  captureCancelIntent,
  captureReactivateIntent,
  captureWinbackIntent,
  clearOfferSource,
} from './lib/attribution';
import { type PublicUser, logout, me } from './lib/auth';
import { loadAuthUiConfig } from './lib/authUiConfig';
// Prism (2026-07-01): the OnboardingGate (a shim .jsx) renders this real TSX
// component when offer.pricing.variant/layout === 'prism'. We register it as a
// window global here so the shim can resolve it (same React instance → hooks
// interoperate). Additive: no existing module references it.
import { PrismShell } from './features/prism/PrismShell';
import SeedProgressBanner from './features/prism/SeedProgressBanner';
// LEA-505: Ein-Klick-Nachkauf-Karte, konsumiert von Account.jsx (shim) — gleicher
// Mechanismus wie SeedProgressBanner.
import ContactPackRefillCard, {
  clearRefillPendingAttempt,
} from './features/billing/ContactPackRefillCard';
import {
  BillingDisabledError,
  BillingError,
  type CancelResult,
  type CancellationFeedback,
  cancelDowngrade,
  applyRetentionDiscount,
  cancelSubscription,
  fetchChurnContext,
  isTransientBillingError,
  openBillingPortal,
  pauseSubscription,
  reactivateSubscription,
  requestVipSetup,
  resumeSubscription,
  scheduleDowngrade,
  startCreditsCheckout,
  startPrismTripwireCheckout,
  startSubscriptionCheckout,
  startWinbackCheckout,
  recordCancellationEvent,
} from './lib/billing';
import { saveLanguage, savePreference } from './lib/preferences';
import { BILLING_PATHS, isAuthPath, isBillingReturnPath } from './lib/routePaths';
import { pushDataLayer } from './lib/analytics';

// ─── Prototype globals ────────────────────────────────────────────────────
declare global {
  interface Window {
    React: typeof React;
    ReactDOM: typeof ReactDOMNS & { createRoot: typeof createRoot };
    __cortexUser: PublicUser | null;
    /** Prism funnel component, consumed by OnboardingGate.jsx for layout 'prism'. */
    PrismShell: typeof PrismShell;
    /** LEA-379 Delivery-UX: Post-Kauf-Seed-Fortschritt, konsumiert von App.jsx. */
    SeedProgressBanner: typeof SeedProgressBanner;
    /** LEA-505: Ein-Klick-Nachkauf von Kontakt-Paketen, konsumiert von Account.jsx. */
    ContactPackRefillCard: typeof ContactPackRefillCard;
    __cortexLogout: () => void;
    __savePreference: typeof savePreference;
    __saveLanguage: typeof saveLanguage;
    /** Start a Stripe Checkout session for a subscription tier, then redirect. */
    __startSubscription: (tier: SubscriptionTier) => Promise<void>;
    /** Start a Stripe Checkout session for a credit pack, then redirect. */
    __startCreditsCheckout: (packSize: CreditPackSize) => Promise<void>;
    /** Start the 49 € Prism tripwire Checkout session, then redirect. */
    __startPrismCheckout: (prismRunId?: string) => Promise<void>;
    /** Open the Stripe Billing Portal. */
    __openBillingPortal: () => Promise<void>;
    /**
     * LEA-299: schedule a downgrade to a lower tier at period end. Resolves with
     * the pending switch (or null on error — a billing-error toast is shown).
     */
    __scheduleDowngrade: (
      tier: SubscriptionTier,
    ) => Promise<{ ok: boolean; pendingTier: SubscriptionTier; effectiveAt: number } | null>;
    /** LEA-299: cancel a pending downgrade. Resolves true on success. */
    __cancelDowngrade: () => Promise<boolean>;
    /**
     * LEA-333: schedule cancellation at period end with optional churn feedback.
     * Resolves with the cancel result (or null on error — a billing-error toast
     * is shown). Feedback is best-effort server-side and never blocks the cancel.
     */
    __cancelSubscription: (feedback?: CancellationFeedback) => Promise<CancelResult | null>;
    /** LEA-333: undo a scheduled cancellation. Resolves true on success. */
    __reactivateSubscription: () => Promise<boolean>;
    /** LEA-333: pause the subscription (freeze billing). Resolves true on success. */
    __pauseSubscription: (months?: 1 | 2 | 3) => Promise<boolean>;
    /**
     * LEA-557: apply the 30% retention coupon (3 months) to the running sub.
     * Resolves true on success; false means the billing-error toast was shown.
     */
    __applyRetentionDiscount: () => Promise<boolean>;
    /** LEA-558: request the founder VIP setup. True on success (incl. repeat). */
    __requestVipSetup: (reasonCategory?: string | null) => Promise<boolean>;
    /**
     * LEA-560: start the win-back reactivation checkout (prefilled session,
     * retention coupon attached). Redirects on success; false = toast shown.
     */
    __startWinbackCheckout: () => Promise<boolean>;
    /** LEA-558: lazy churn context for the cancel modal (null = unknown). */
    __fetchChurnContext: () => Promise<{ leadsDelivered: number | null }>;
    /**
     * LEA-561: record how one run of the cancellation modal ended. Best-effort
     * measurement — never throws, never blocks the user's action.
     */
    __recordCancellationEvent: (input: {
      flowId: string;
      outcome: string;
      offeredLevers?: string[];
      acceptedLever?: string | null;
      lastStep?: string;
      reasonCategory?: string | null;
      churnFeedbackId?: string | null;
    }) => void;
    /** LEA-333: resume a paused subscription. Resolves true on success. */
    __resumeSubscription: () => Promise<boolean>;
    /** i18n translate function — returns localised string for the current language. */
    t: (key: string, vars?: Record<string, string>) => string;
    /** i18n hook — re-renders the component when the language changes. */
    useLang: () => string;
  }
}
window.React = React;
window.ReactDOM = Object.assign({}, ReactDOMNS, { createRoot });

// Minimal i18n stubs so auth pages (which render before i18n.jsx loads) don't crash.
// The real i18n.jsx module overwrites these once it loads for authenticated users.
if (!window.t) {
  const fallback: Record<string, Record<string, string>> = {
    de: {
      'auth.login.title': 'Willkommen zurück',
      'auth.login.sub': 'Melden Sie sich bei Ihrem Konto an, um auf Ihr Konto zuzugreifen.',
      'auth.login.email': 'E-Mail',
      'auth.login.password': 'Passwort',
      'auth.login.remember': 'Eingeloggt bleiben',
      'auth.login.submit': 'Anmelden',
      'auth.login.submitting': 'Anmelden …',
      'auth.login.forgot': 'Passwort vergessen?',
      'auth.login.noAccount': 'Noch kein Konto?',
      'auth.login.signUp': 'Registrieren',
      'auth.login.or': 'Oder',
      'auth.login.google': 'Mit Google anmelden',
      'auth.login.googleBusy': 'Anmeldung mit Google …',
      'auth.login.microsoft': 'Mit Microsoft anmelden',
      'auth.login.microsoftBusy': 'Anmeldung mit Microsoft …',
      'auth.login.errEmpty': 'Bitte fülle alle Felder aus.',
      'auth.login.errEmail': 'Ungültige E-Mail-Adresse.',
      'auth.login.errUnverified': 'Bitte bestätige zuerst deine E-Mail-Adresse.',
      'auth.login.errCredentials': 'E-Mail oder Passwort ist falsch.',
      'auth.login.errGeneric': 'Login fehlgeschlagen. Bitte versuche es erneut.',
      'auth.login.hidePassword': 'Passwort verbergen',
      'auth.login.showPassword': 'Passwort anzeigen',
      'auth.login.docTitle': 'Anmelden · Leadscraper',
      'auth.register.title': 'Kostenlosen Account erstellen',
      'auth.register.sub': 'Teste wie einfach präzise Leadlisten heute dank KI-Agenten generiert werden können.',
      'auth.register.firstName': 'Vorname',
      'auth.register.lastName': 'Nachname',
      'auth.register.email': 'E-Mail',
      'auth.register.password': 'Passwort',
      'auth.register.passwordConfirm': 'Passwort bestätigen',
      'auth.register.submit': 'Konto erstellen',
      'auth.register.submitting': 'Wird erstellt …',
      'auth.register.haveAccount': 'Bereits ein Konto?',
      'auth.register.signIn': 'Anmelden',
      'auth.register.or': 'Oder',
      'auth.register.google': 'Mit Google registrieren',
      'auth.register.googleBusy': 'Registrierung mit Google …',
      'auth.register.microsoft': 'Mit Microsoft registrieren',
      'auth.register.microsoftBusy': 'Registrierung mit Microsoft …',
      'auth.register.errEmpty': 'Bitte fülle alle Felder aus.',
      'auth.register.errEmail': 'Ungültige E-Mail-Adresse.',
      'auth.register.errPwShort': 'Passwort muss mindestens 8 Zeichen lang sein.',
      'auth.register.errPwMismatch': 'Passwörter stimmen nicht überein.',
      'auth.register.hidePassword': 'Passwort verbergen',
      'auth.register.showPassword': 'Passwort anzeigen',
      'auth.register.docTitle': 'Registrieren · Leadscraper',
      'auth.reset.docTitle': 'Passwort zurücksetzen · Leadscraper',
      'auth.reset.title': 'Passwort zurücksetzen',
      'auth.reset.sub': 'Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.',
      'auth.reset.emailLabel': 'E-Mail*',
      'auth.reset.emailPlaceholder': 'Geben Sie Ihre E-Mail-Adresse ein',
      'auth.reset.errInvalidEmail': 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      'auth.reset.errRequest': 'Anfrage fehlgeschlagen. Bitte versuchen Sie es erneut.',
      'auth.reset.sending': 'Wird gesendet …',
      'auth.reset.send': 'Link senden',
      'auth.reset.haveAccount': 'Sie haben bereits ein Konto?',
      'auth.reset.signIn': 'Anmelden',
      'auth.reset.sentTitle': 'E-Mail gesendet',
      'auth.reset.sentSub': 'Falls ein Konto mit dieser E-Mail existiert, haben wir Ihnen einen Link zum Zurücksetzen gesendet. Prüfen Sie Ihren Posteingang (und ggf. Spam).',
      'auth.reset.backToLogin': 'Zurück zur Anmeldung',
      'auth.reset.newPwTitle': 'Neues Passwort festlegen',
      'auth.reset.newPwSub': 'Wählen Sie ein neues Passwort für Ihr Konto.',
      'auth.reset.newPwLabel': 'Neues Passwort*',
      'auth.reset.newPwPlaceholder': 'Mindestens 8 Zeichen',
      'auth.reset.confirmLabel': 'Passwort bestätigen*',
      'auth.reset.confirmPlaceholder': 'Passwort erneut eingeben',
      'auth.reset.errPwShort': 'Das Passwort muss mindestens 8 Zeichen lang sein.',
      'auth.reset.errPwMismatch': 'Passwörter stimmen nicht überein.',
      'auth.reset.saving': 'Wird gespeichert …',
      'auth.reset.resetBtn': 'Passwort zurücksetzen',
      'auth.reset.hidePassword': 'Passwort verbergen',
      'auth.reset.showPassword': 'Passwort anzeigen',
      'auth.reset.doneTitle': 'Passwort erfolgreich geändert',
      'auth.reset.doneSub': 'Sie können sich jetzt mit Ihrem neuen Passwort anmelden.',
      'auth.reset.goToLogin': 'Zur Anmeldung',
      'auth.reset.invalidTitle': 'Link ungültig oder abgelaufen',
      'auth.reset.invalidSub': 'Dieser Link zum Zurücksetzen des Passworts ist nicht mehr gültig. Bitte fordern Sie einen neuen an.',
      'auth.reset.requestNew': 'Neuen Link anfordern',
      'auth.verify.docTitle': 'E-Mail bestätigen · Leadscraper',
      'auth.verify.title': 'E-Mail bestätigen',
      'auth.verify.verifying': 'E-Mail wird bestätigt …',
      'auth.verify.success': 'Erfolgreich!',
      'auth.verify.error': 'Bestätigung fehlgeschlagen',
      'auth.verify.noToken': 'Bestätigungslink fehlt',
      'auth.verify.verifyingSub': 'Einen Moment bitte.',
      'auth.verify.successSub': 'Ihre E-Mail-Adresse wurde bestätigt. Sie werden zum Dashboard weitergeleitet …',
      'auth.verify.defaultError': 'Ein Fehler ist aufgetreten.',
      'auth.verify.noTokenSub': 'Klicken Sie auf den Link in der E-Mail, die wir Ihnen gesendet haben. Falls Sie keine E-Mail erhalten haben, können Sie eine neue anfordern.',
      'auth.verify.goToDashboard': 'Sofort zum Dashboard',
      'auth.verify.resent': 'E-Mail erneut gesendet ✓',
      'auth.verify.resending': 'Wird gesendet …',
      'auth.verify.resendError': 'Neue Bestätigungs-E-Mail anfordern',
      'auth.verify.resendNoToken': 'Bestätigungs-E-Mail erneut senden',
      'auth.verify.toLogin': 'Zur Anmeldung',
      'auth.verify.resendFailed': 'E-Mail konnte nicht erneut gesendet werden.',
      'auth.shell.nextSlide': 'Nächste Folie',
    },
    en: {
      'auth.login.title': 'Welcome back',
      'auth.login.sub': 'Sign in to your account to access your dashboard.',
      'auth.login.email': 'Email',
      'auth.login.password': 'Password',
      'auth.login.remember': 'Stay logged in',
      'auth.login.submit': 'Sign in',
      'auth.login.submitting': 'Signing in …',
      'auth.login.forgot': 'Forgot password?',
      'auth.login.noAccount': 'No account yet?',
      'auth.login.signUp': 'Sign up',
      'auth.login.or': 'Or',
      'auth.login.google': 'Sign in with Google',
      'auth.login.googleBusy': 'Signing in with Google …',
      'auth.login.microsoft': 'Sign in with Microsoft',
      'auth.login.microsoftBusy': 'Signing in with Microsoft …',
      'auth.login.errEmpty': 'Please fill in all fields.',
      'auth.login.errEmail': 'Invalid email address.',
      'auth.login.errUnverified': 'Please confirm your email address first.',
      'auth.login.errCredentials': 'Email or password is incorrect.',
      'auth.login.errGeneric': 'Login failed. Please try again.',
      'auth.login.hidePassword': 'Hide password',
      'auth.login.showPassword': 'Show password',
      'auth.login.docTitle': 'Sign in · Leadscraper',
      'auth.register.title': 'Create free account',
      'auth.register.sub': 'See how easy it is to generate precise lead lists with AI agents today.',
      'auth.register.firstName': 'First name',
      'auth.register.lastName': 'Last name',
      'auth.register.email': 'Email',
      'auth.register.password': 'Password',
      'auth.register.passwordConfirm': 'Confirm password',
      'auth.register.submit': 'Create account',
      'auth.register.submitting': 'Creating …',
      'auth.register.haveAccount': 'Already have an account?',
      'auth.register.signIn': 'Sign in',
      'auth.register.or': 'Or',
      'auth.register.google': 'Sign up with Google',
      'auth.register.googleBusy': 'Signing up with Google …',
      'auth.register.microsoft': 'Sign up with Microsoft',
      'auth.register.microsoftBusy': 'Signing up with Microsoft …',
      'auth.register.errEmpty': 'Please fill in all fields.',
      'auth.register.errEmail': 'Invalid email address.',
      'auth.register.errPwShort': 'Password must be at least 8 characters long.',
      'auth.register.errPwMismatch': 'Passwords do not match.',
      'auth.register.hidePassword': 'Hide password',
      'auth.register.showPassword': 'Show password',
      'auth.register.docTitle': 'Sign up · Leadscraper',
      'auth.reset.docTitle': 'Reset password · Leadscraper',
      'auth.reset.title': 'Reset password',
      'auth.reset.sub': 'Enter your email address and we will send you a link to reset your password.',
      'auth.reset.emailLabel': 'Email*',
      'auth.reset.emailPlaceholder': 'Enter your email address',
      'auth.reset.errInvalidEmail': 'Please enter a valid email address.',
      'auth.reset.errRequest': 'Request failed. Please try again.',
      'auth.reset.sending': 'Sending …',
      'auth.reset.send': 'Send link',
      'auth.reset.haveAccount': 'Already have an account?',
      'auth.reset.signIn': 'Sign in',
      'auth.reset.sentTitle': 'Email sent',
      'auth.reset.sentSub': 'If an account with this email exists, we have sent you a reset link. Check your inbox (and spam folder).',
      'auth.reset.backToLogin': 'Back to sign in',
      'auth.reset.newPwTitle': 'Set new password',
      'auth.reset.newPwSub': 'Choose a new password for your account.',
      'auth.reset.newPwLabel': 'New password*',
      'auth.reset.newPwPlaceholder': 'At least 8 characters',
      'auth.reset.confirmLabel': 'Confirm password*',
      'auth.reset.confirmPlaceholder': 'Enter password again',
      'auth.reset.errPwShort': 'Password must be at least 8 characters long.',
      'auth.reset.errPwMismatch': 'Passwords do not match.',
      'auth.reset.saving': 'Saving …',
      'auth.reset.resetBtn': 'Reset password',
      'auth.reset.hidePassword': 'Hide password',
      'auth.reset.showPassword': 'Show password',
      'auth.reset.doneTitle': 'Password successfully changed',
      'auth.reset.doneSub': 'You can now sign in with your new password.',
      'auth.reset.goToLogin': 'Go to sign in',
      'auth.reset.invalidTitle': 'Link invalid or expired',
      'auth.reset.invalidSub': 'This password reset link is no longer valid. Please request a new one.',
      'auth.reset.requestNew': 'Request new link',
      'auth.verify.docTitle': 'Verify email · Leadscraper',
      'auth.verify.title': 'Verify email',
      'auth.verify.verifying': 'Verifying email …',
      'auth.verify.success': 'Success!',
      'auth.verify.error': 'Verification failed',
      'auth.verify.noToken': 'Verification link missing',
      'auth.verify.verifyingSub': 'One moment please.',
      'auth.verify.successSub': 'Your email has been verified. Redirecting to dashboard …',
      'auth.verify.defaultError': 'An error occurred.',
      'auth.verify.noTokenSub': "Click the link in the email we sent you. If you didn't receive one, you can request a new one.",
      'auth.verify.goToDashboard': 'Go to dashboard now',
      'auth.verify.resent': 'Email resent ✓',
      'auth.verify.resending': 'Sending …',
      'auth.verify.resendError': 'Request new verification email',
      'auth.verify.resendNoToken': 'Resend verification email',
      'auth.verify.toLogin': 'Go to sign in',
      'auth.verify.resendFailed': 'Email could not be resent.',
      'auth.shell.nextSlide': 'Next slide',
    },
  };
  // LEA-242 (Shanky 2026-05-06): honor `?lang=de|en` from the URL ABOVE
  // localStorage. Auth pages (reset-password, verify-email) are reached
  // exclusively via Postmark email links that always carry `&lang=...`
  // matching the user's stored language at send-time. Without this, a
  // stale `ls_lang=en` in localStorage from prior browsing wins over a
  // fresh German reset link, and the page renders English copy on a
  // German user — see screenshot in chat 2026-05-07. Logged-in user
  // language (window.__cortexUser) still wins over both.
  const getLang = () => {
    try {
      const fromUser = (window as unknown as { __cortexUser?: { language?: string } }).__cortexUser?.language;
      if (fromUser === 'en' || fromUser === 'de') return fromUser;
      const fromUrl = new URLSearchParams(window.location.search).get('lang');
      if (fromUrl === 'en' || fromUrl === 'de') {
        try { localStorage.setItem('ls_lang', fromUrl); } catch {}
        return fromUrl;
      }
      return localStorage.getItem('ls_lang') || 'de';
    } catch { return 'de'; }
  };
  // Boot-time copy used outside React (alerts, bootError) — same fallback
  // approach as window.t but standalone so it works even when the stubs
  // get replaced by i18n.jsx mid-flight.
  fallback.de['boot.connectionError'] = 'Verbindungsfehler';
  fallback.de['boot.connectionErrorDesc'] = 'Konnte keine Verbindung zum Server herstellen: {msg}';
  fallback.de['boot.retry'] = 'Erneut versuchen';
  fallback.de['boot.reconnecting'] = 'Verbindung wird wiederhergestellt … (Versuch {n})';
  fallback.de['billing.disabled'] = 'Bezahlungen sind aktuell nicht verfügbar. Bitte versuche es später erneut.';
  fallback.de['billing.unknownError'] = 'Unbekannter Fehler beim Bezahlvorgang.';
  fallback.de['billing.portalNoCustomer'] = 'Subscription-Verwaltung ist erst nach dem ersten Kauf verfügbar.';
  fallback.de['billing.errorTitle'] = 'Etwas ist schiefgelaufen';
  fallback.de['billing.errorClose'] = 'Schließen';
  fallback.de['billing.errorRetry'] = 'Erneut versuchen';
  fallback.de['billing.transientError'] =
    'Vorübergehend nicht erreichbar. Bitte versuche es gleich noch einmal.';
  fallback.en['boot.connectionError'] = 'Connection error';
  fallback.en['boot.connectionErrorDesc'] = 'Could not connect to the server: {msg}';
  fallback.en['boot.retry'] = 'Try again';
  fallback.en['boot.reconnecting'] = 'Reconnecting… (attempt {n})';
  fallback.en['billing.disabled'] = 'Payments are currently unavailable. Please try again later.';
  fallback.en['billing.unknownError'] = 'Unknown billing error.';
  fallback.en['billing.portalNoCustomer'] = 'Subscription management is only available after your first purchase.';
  fallback.en['billing.errorTitle'] = 'Something went wrong';
  fallback.en['billing.errorClose'] = 'Close';
  fallback.en['billing.errorRetry'] = 'Try again';
  fallback.en['billing.transientError'] = 'Temporarily unavailable. Please try again in a moment.';

  window.t = (key: string, vars?: Record<string, string>) => {
    const lang = getLang();
    let str = fallback[lang]?.[key] ?? fallback.de[key] ?? key;
    if (vars) Object.entries(vars).forEach(([k, v]) => { str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), v); });
    return str;
  };
  window.useLang = () => getLang();
}
window.__cortexUser = null;
window.__cortexLogout = () => {
  clearCustomerUpdateLink();
  clearOfferSource();
  // LEA-430 #2: den persistierten Prism-Funnel-/Rechnungs-Stand beim Logout
  // wegräumen. sessionStorage überlebt sonst den location.replace unten → ein
  // nachfolgender Login im selben Tab (geteilter Rechner) würde Name/Adresse/
  // USt-IdNr. + prismRunId des Vor-Users restaurieren. Die uid-Bindung im
  // Snapshot fängt das zwar zusätzlich ab, das aktive Löschen ist die erste
  // Verteidigungslinie.
  clearPrismCheckoutState();
  // LEA-505: ein ungeklärter Nachkauf-Klick gehört dem abgemeldeten User.
  clearRefillPendingAttempt();
  void logout().finally(() => {
    window.location.replace('/login');
  });
};

// V1.26.0 (Janik 2026-05-05) side-fix: ?impersonate=<uid> support in
// Customer-FE. Existed before only in the prototype-tree; without this,
// "Open as user" from the admin panel landed on the login page in
// local-dev (no cookie session). Boot reads the param once, stores it
// in sessionStorage, and a global fetch-monkey-patch sends it as
// X-Cortex-User on every API request. Backend's requireAuth accepts
// this in NODE_ENV !== production for any real UID (see
// apps/api/src/middleware/requireAuth.ts:119).
//
// Production-safe: NODE_ENV=production on DO ignores the header for
// non-admin sessions, so an attacker copying ?impersonate=<uid> into a
// real-world URL gets nothing. Only useful in local dev + Mini.
(() => {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get('impersonate');
  if (fromUrl) {
    try {
      sessionStorage.setItem('cortex_impersonate_uid', fromUrl);
    } catch {
      // sessionStorage disabled (Safari private mode etc.) — non-fatal.
    }
    // Strip the param from the visible URL so reloads / shares don't
    // carry it. The sessionStorage stays for the tab's lifetime.
    url.searchParams.delete('impersonate');
    window.history.replaceState({}, '', url.toString());
  }

  // Monkey-patch fetch ONCE so every API call (regardless of which
  // fetch-helper file uses it) carries X-Cortex-User. Avoids having
  // to edit auth.ts + api.jsx + crm.ts + billing.ts + preferences.ts
  // separately + risk forgetting one.
  const originalFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    let impersonateUid: string | null = null;
    try {
      impersonateUid = sessionStorage.getItem('cortex_impersonate_uid');
    } catch {
      // ignore
    }

    if (!impersonateUid) return originalFetch(input, init);

    // Only inject for our own /api/* endpoints — never leak the header
    // to third-party hosts (analytics, GTM, fonts, Stripe.js, etc.).
    const urlStr =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input instanceof Request
            ? input.url
            : '';
    const isOwnApi =
      urlStr.startsWith('/api/') ||
      urlStr.startsWith(`${window.location.origin}/api/`);
    if (!isOwnApi) return originalFetch(input, init);

    const mergedHeaders = new Headers(init?.headers);
    // Header-Name MUSS X-Cortex-User sein — das ist der einzige Name, den
    // requireAuth (Dev-Bypass + Admin-Impersonation) liest. Bis 2026-07-03
    // stand hier X-Leadscraper-User: kein API-Codepfad hat den je gelesen,
    // ?impersonate= war in der Vite-App dadurch seit Mai stumm kaputt.
    if (!mergedHeaders.has('X-Cortex-User')) {
      mergedHeaders.set('X-Cortex-User', impersonateUid);
    }
    return originalFetch(input, { ...init, headers: mergedHeaders });
  }) as typeof window.fetch;
})();
window.__savePreference = savePreference;
window.__saveLanguage = saveLanguage;

// ─── Billing redirects ────────────────────────────────────────────────────
// Each helper hits the API, then `window.location.href = url` to hand off
// to Stripe. A failure surfaces a friendly in-app modal and stays on the
// current page so the user doesn't lose context.
//
// LEA-261 (Janik 2026-05-13): replaced native `alert()` with a custom event
// (`billing-error`) so App.jsx can render an in-brand, responsive modal.
// Native `alert()` rendered as "my.leadscraper.de says: …" which is
// out-of-brand on desktop and broken-looking on mobile.
function billingErrorMessage(err: unknown): string {
  if (err instanceof BillingDisabledError) {
    return window.t('billing.disabled');
  }
  // LEA-333 undo-504: a transient gateway/Stripe blip (502/503/504) gets a
  // friendly "try again in a moment" message instead of the raw
  // "Billing request failed (504)" — the modal also offers a Try-again button.
  if (isTransientBillingError(err)) {
    return window.t('billing.transientError');
  }
  // Paketkauf auf einem abgelaufenen Konto: die API schickt den lokalisierten Grund
  // mit (wie die Sperren in /api/scraper) — den zeigen, nicht den nackten Code.
  if (
    err instanceof BillingError &&
    err.code === 'subscription_ended' &&
    typeof err.body.message === 'string'
  ) {
    return err.body.message;
  }
  // 15.10.2026: seit die Meldung über den Zwangs-Gates liegt (App.jsx), sahen Kunden hier rohe
  // Codes („already_subscribed_to_tier", „Billing request failed (500)"). Bekannte Codes bekommen
  // ihren Text, alles andere den allgemeinen; der Code bleibt in der Konsole.
  if (err instanceof BillingError) {
    const code = err.code ?? '';
    if (['already_subscribed', 'already_subscribed_to_tier', 'already_on_tier'].includes(code)) {
      return window.t('billing.alreadySubscribed');
    }
    if (code === 'subscription_canceling' || code === 'cancellation_scheduled') {
      return window.t('billing.reactivateFirst');
    }
    if (code === 'no_stripe_customer') return window.t('billing.portalNoCustomer');
    console.warn('[billing] unmapped error', err.status, code || err.message);
    return window.t('billing.unknownError');
  }
  // fetch selbst ist gescheitert (offline, DNS, abgebrochen): vorübergehend.
  if (err instanceof TypeError) return window.t('billing.transientError');
  return window.t('billing.unknownError');
}

// LEA-333 undo-504: `onRetry` (optional) lets the in-app modal offer a Try-again
// button for idempotent, recoverable actions. CustomEvent detail carries the
// callback same-realm; App.jsx invokes it then closes the modal.
function showBillingError(message: string, onRetry?: () => void): void {
  window.dispatchEvent(new CustomEvent('billing-error', { detail: { message, onRetry } }));
}

window.__startSubscription = async (tier) => {
  try {
    const { url } = await startSubscriptionCheckout(tier);
    // LEA-319 funnel event: the Stripe session exists once we have a URL,
    // fired before the redirect hands the page off.
    pushDataLayer('checkout_session_created', { tier });
    window.location.href = url;
  } catch (err) {
    console.error('[billing] subscription checkout failed:', err);
    showBillingError(billingErrorMessage(err));
  }
};

window.__startCreditsCheckout = async (packSize) => {
  try {
    const { url } = await startCreditsCheckout(packSize);
    window.location.href = url;
  } catch (err) {
    console.error('[billing] credits checkout failed:', err);
    showBillingError(billingErrorMessage(err));
  }
};

window.__startPrismCheckout = async (prismRunId) => {
  try {
    const { url } = await startPrismTripwireCheckout(prismRunId);
    pushDataLayer('checkout_session_created', { kind: 'prism_tripwire' });
    window.location.href = url;
  } catch (err) {
    console.error('[billing] prism tripwire checkout failed:', err);
    showBillingError(billingErrorMessage(err));
  }
};

window.__openBillingPortal = async () => {
  try {
    const { url } = await openBillingPortal();
    window.location.href = url;
  } catch (err) {
    console.error('[billing] portal open failed:', err);
    if (err instanceof Error && /no_stripe_customer/.test(err.message)) {
      showBillingError(window.t('billing.portalNoCustomer'));
    } else {
      showBillingError(billingErrorMessage(err));
    }
  }
};

// LEA-299: schedule a downgrade at period end (no redirect — the user stays in
// the app; the caller shows a confirmation toast and refreshes billing state).
window.__scheduleDowngrade = async (tier) => {
  try {
    return await scheduleDowngrade(tier);
  } catch (err) {
    console.error('[billing] schedule downgrade failed:', err);
    showBillingError(billingErrorMessage(err));
    return null;
  }
};

window.__cancelDowngrade = async () => {
  try {
    await cancelDowngrade();
    return true;
  } catch (err) {
    console.error('[billing] cancel downgrade failed:', err);
    showBillingError(billingErrorMessage(err));
    return false;
  }
};

// LEA-333: inline cancellation (schedules cancel_at_period_end, no redirect —
// the caller's modal shows transparency + a confirmation, then refreshes billing
// state). Optional churn feedback rides along and is best-effort server-side.
window.__cancelSubscription = async (feedback) => {
  try {
    return await cancelSubscription(feedback);
  } catch (err) {
    console.error('[billing] cancel subscription failed:', err);
    showBillingError(billingErrorMessage(err));
    return null;
  }
};

window.__startWinbackCheckout = async () => {
  try {
    const res = await startWinbackCheckout();
    if (res?.url) {
      window.location.assign(res.url);
      return true;
    }
    return false;
  } catch (err) {
    console.error('[billing] winback checkout failed:', err);
    showBillingError(billingErrorMessage(err));
    return false;
  }
};

window.__requestVipSetup = async (reasonCategory) => {
  try {
    const res = await requestVipSetup(reasonCategory);
    return Boolean(res?.ok);
  } catch (err) {
    console.error('[billing] vip setup request failed:', err);
    showBillingError(billingErrorMessage(err));
    return false;
  }
};

window.__fetchChurnContext = () => fetchChurnContext();

window.__applyRetentionDiscount = async () => {
  try {
    const res = await applyRetentionDiscount();
    // alreadyApplied ist derselbe Zustand, kein Fehler.
    return Boolean(res?.ok);
  } catch (err) {
    console.error('[billing] retention discount failed:', err);
    showBillingError(billingErrorMessage(err));
    return false;
  }
};

window.__recordCancellationEvent = (input) => {
  // Fire-and-forget by design: callers include an unmount cleanup and a
  // pagehide listener, neither of which can await.
  void recordCancellationEvent(input);
};

window.__reactivateSubscription = async () => {
  try {
    await reactivateSubscription();
    return true;
  } catch (err) {
    console.error('[billing] reactivate subscription failed:', err);
    showBillingError(billingErrorMessage(err), () => {
      void window.__reactivateSubscription().then((ok) => {
        if (ok) window.dispatchEvent(new CustomEvent('cortex:credits-stale'));
      });
    });
    return false;
  }
};

// LEA-333: pause / resume (freeze + unfreeze billing; no redirect — the caller
// refreshes billing state after).
window.__pauseSubscription = async (months) => {
  try {
    await pauseSubscription(months);
    return true;
  } catch (err) {
    console.error('[billing] pause subscription failed:', err);
    showBillingError(billingErrorMessage(err), () => {
      void window.__pauseSubscription(months).then((ok) => {
        if (ok) window.dispatchEvent(new CustomEvent('cortex:credits-stale'));
      });
    });
    return false;
  }
};

window.__resumeSubscription = async () => {
  try {
    await resumeSubscription();
    return true;
  } catch (err) {
    console.error('[billing] resume subscription failed:', err);
    showBillingError(billingErrorMessage(err), () => {
      void window.__resumeSubscription().then((ok) => {
        if (ok) window.dispatchEvent(new CustomEvent('cortex:credits-stale'));
      });
    });
    return false;
  }
};

// ─── Auth gate ────────────────────────────────────────────────────────────
// AuthPage is dynamically imported: it transitively pulls in the Firebase
// client SDK (Google/Microsoft sign-in), which a logged-in user hitting the
// dashboard never needs. Keeping it out of the entry chunk shrinks the
// blocking first-load download; the boot splash stays visible while it loads.
//
// Login/Registrierung (Revision 19): die Optik wird VOR dem ersten Render
// entschieden und parallel zum Chunk-Download geholt — der Boot-Splash deckt
// die Wartezeit ab. Als fester Prop, nie als State: die Formulare mounten genau
// einmal mit ihrer endgueltigen Optik. Siehe lib/authUiConfig.ts.
async function bootAuth(): Promise<void> {
  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('#root not found in DOM');
  const [{ default: AuthPage }, ui] = await Promise.all([import('./AuthPage'), loadAuthUiConfig()]);
  console.info('[auth-ui] design=%s source=%s', ui.authR19Enabled ? 'r19' : 'classic', ui.source);
  createRoot(rootEl).render(
    <React.StrictMode>
      <AuthPage
        presentation={ui.authR19Enabled ? 'r19' : 'classic'}
        onAuthed={() => {
          window.location.replace('/');
        }}
      />
    </React.StrictMode>,
  );
}

// ─── Billing return page (after Stripe Checkout) ──────────────────────────
async function bootBillingReturn(mode: 'success' | 'cancel'): Promise<void> {
  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('#root not found in DOM');
  const { default: BillingReturnPage } = await import('./BillingReturnPage');
  createRoot(rootEl).render(
    <React.StrictMode>
      <BillingReturnPage
        mode={mode}
        onContinue={() => {
          window.location.replace('/');
        }}
      />
    </React.StrictMode>,
  );
}

// Boot-Verbindungsfehler war bis 2026-08-05 eine SACKGASSE: ein statisches
// Banner, dessen einziger Ausweg ein voller Reload war (der den ganzen Bundle
// erneut vom selben, gerade wackelnden Origin zieht).
//
// Gemessen auf PROD am 2026-08-05: die PROD-API-App faellt periodisch aus dem
// Envoy-Upstream-Pool von DigitalOcean (`x-do-failure-code: UH`,
// `no_healthy_upstream`, `x-do-orig-status: 503`) — Fenster von 19s, 23s, 36s
// und ~80s. Cloudflare rebrandet das zu einem 504, das in ~0.3-1s zurueckkommt.
// me() verbraucht seine 5 Versuche gegen solche SCHNELLEN Fehler in ~11s,
// landet also garantiert im Banner, obwohl der Origin Sekunden spaeter wieder
// da ist. Genau das hat Om wiederholt gesehen.
//
// Deshalb: keine Sackgasse mehr, sondern ein Screen der sich selbst heilt. Er
// rendert in seinen EIGENEN Root, den wir vor dem Weiterbooten unmounten —
// App.jsx macht spaeter selbst createRoot() auf #root, ein zweiter Root auf
// demselben Container waere ein Re-Entrancy-Bug.
type ReconnectScreen = {
  render: (attempt: number) => void;
  waitOrNudge: (ms: number) => Promise<void>;
  dispose: () => void;
};

function bootReconnect(message: string): ReconnectScreen | null {
  const rootEl = document.getElementById('root');
  if (!rootEl) return null;
  const root = createRoot(rootEl);
  let nudge: (() => void) | null = null;
  const fireNudge = (): void => {
    const n = nudge;
    nudge = null;
    n?.();
  };
  const onOnline = (): void => fireNudge();
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') fireNudge();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);

  return {
    render(attempt: number): void {
      root.render(
        <div className="min-h-screen flex items-center justify-center bg-surface p-6 sm:p-8">
          <div className="w-full max-w-md text-center space-y-4">
            <div className="text-[18px] font-semibold text-ink">
              {window.t('boot.connectionError')}
            </div>
            <div className="text-[14px] text-ink-muted">{message}</div>
            <div className="text-[13px] text-ink-muted">
              {window.t('boot.reconnecting', { n: String(attempt) })}
            </div>
            <button
              type="button"
              onClick={fireNudge}
              className="btn btn-md btn-primary whitespace-nowrap"
            >
              {window.t('boot.retry')}
            </button>
          </div>
        </div>,
      );
    },
    waitOrNudge(ms: number): Promise<void> {
      return new Promise<void>((resolve) => {
        const timer = window.setTimeout(() => {
          nudge = null;
          resolve();
        }, ms);
        // Ein Nudge (Button / Tab wieder sichtbar / wieder online) kuerzt die
        // Wartezeit ab — aber nie auf 0: eine Flotte von Tabs, die aus DEMSELBEN
        // Ausfall aufwacht, darf den gerade zurueckkehrenden Origin nicht im
        // Gleichschritt treffen (gleicher Grund wie das jitter() in auth.ts).
        nudge = () => {
          window.clearTimeout(timer);
          window.setTimeout(resolve, Math.round(Math.random() * 1200));
        };
      });
    },
    dispose(): void {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      root.unmount();
    },
  };
}

// 5s → 30s Deckel, ±25% Jitter. Spannt die auf PROD gemessenen 19-80s-Fenster
// ab, ohne einen sich erholenden Origin zu hammern.
const RECONNECT_BACKOFF_MS = [5_000, 8_000, 12_000, 20_000, 30_000];
const reconnectDelay = (attempt: number): number => {
  const i = Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1);
  const base = RECONNECT_BACKOFF_MS[i] ?? 30_000;
  return Math.round(base * (0.75 + Math.random() * 0.5));
};

// LEA-339: a "positive" language choice exists when the visitor arrived via a
// localized URL prefix (/en, /de, …), a stored ls_lang from a prior visit, or
// a logged-in server preference. An explicit ?lang= is handled separately in
// the isAuthPath branch (so it can be *applied*, not merely detected). Absent
// all of these, the auth pages fall back to English (see that branch) instead
// of the global DEFAULT_LANG ('de'), which is left untouched so the app-wide
// string-fallback chain (target → de → key) is unchanged.
function hasExplicitLangSignal(urlFirstSeg: string): boolean {
  if (urlFirstSeg && isSupportedLang(urlFirstSeg)) return true;
  try {
    const stored = localStorage.getItem('ls_lang');
    if (stored && isSupportedLang(stored)) return true;
  } catch {
    /* localStorage blocked (Safari private mode) — ignore */
  }
  const u = (window as unknown as { __cortexUser?: { language?: string } | null }).__cortexUser;
  return !!(u?.language && isSupportedLang(u.language));
}

// LEA-242: explicit ?lang= from a Postmark link (reset/verify emails always
// carry it, matching the user's send-time language). createLangStore() never
// reads ?lang=, so apply it here for the auth render — display-only, so it
// wins over a stale ls_lang without overwriting a stored/server preference.
function readLangParam(): Lang | null {
  try {
    const p = new URLSearchParams(window.location.search).get('lang');
    return p && isSupportedLang(p) ? p : null;
  } catch {
    return null;
  }
}

// LEA-launch (Janik 2026-07-05): browser-locale fallback for the auth-page display
// resolver — mirrors LangStore's navigator step (base-language match). Display-only,
// consulted solely in the no-explicit-signal branch, so a FR/ES/IT/NL/PL browser
// sees its own language instead of German; EN + unmatched browsers → null (keeps
// the LEA-339 English fallback). Never overrides a real signal or persists.
function navigatorLangParam(): Lang | null {
  try {
    const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const raw of langs) {
      const base = raw.toLowerCase().split(/[-_]/)[0];
      if (isSupportedLang(base)) return base;
    }
  } catch {
    /* non-browser */
  }
  return null;
}

async function bootApp(): Promise<void> {
  captureCustomerUpdateLink();
  // LEA-574: Der Reaktivieren-Link aus der Kuendigungs-Mail (`/?reactivate=1`).
  // MUSS vor dem isAuthPath-Branch und vor me() laufen, sonst ist der Param nach
  // dem Login-Bounce weg: AuthPage replaceState't jeden Nicht-Auth-Pfad auf
  // /login, und nach dem Login macht main.tsx ein location.replace('/').
  captureReactivateIntent();
  // LEA-556: derselbe Mechanismus fuer den Kuendigen-Link aus der
  // Trial-Erinnerungs-Mail (`/?cancel=1`).
  captureCancelIntent();
  // LEA-560: Win-back-Mail-Link.
  captureWinbackIntent();

  // --- Locale prefix detection (LEA-267) ---
  // URLs like /en/login, /fr/pricing strip the locale prefix, set the
  // language, and proceed with the clean path. German (default) has no
  // prefix — /login, /pricing etc. Unknown prefixes redirect to /.
  const rawPath = window.location.pathname;
  const firstSeg = rawPath.split('/')[1] || '';
  let path = rawPath;

  if (firstSeg && isSupportedLang(firstSeg) && firstSeg !== DEFAULT_LANG) {
    path = rawPath.slice(firstSeg.length + 1) || '/';
    LangStore.set(firstSeg);
    document.documentElement.lang = firstSeg;
  } else if (firstSeg === DEFAULT_LANG) {
    // /de/... → redirect to /... (German has no prefix)
    const clean = rawPath.slice(3) || '/';
    window.history.replaceState(null, '', clean + window.location.search);
    path = clean;
  } else {
    document.documentElement.lang = LangStore.get();
  }

  // /dev/pricing-demo → interner Pricing-v2-Sandbox (kein Auth, kein API-Call).
  // Wird nicht im Customer-UI verlinkt — siehe PricingDemoPage.tsx.
  // Dynamisch geladen: zieht Lead-UI rein, die kein Customer-Pfad braucht.
  if (path === '/dev/pricing-demo') {
    const rootEl = document.getElementById('root');
    if (!rootEl) throw new Error('#root not found in DOM');
    const { default: PricingDemoPage } = await import('./PricingDemoPage');
    createRoot(rootEl).render(
      <React.StrictMode>
        <PricingDemoPage />
      </React.StrictMode>,
    );
    return;
  }

  // /dev/prism-demo → interner "Prism" Trial-Paywall-Funnel-Sandbox (kein Auth,
  // kein API-Call). Wird nicht im Customer-UI verlinkt — siehe PrismDemoPage.tsx.
  if (path === '/dev/prism-demo') {
    const rootEl = document.getElementById('root');
    if (!rootEl) throw new Error('#root not found in DOM');
    const { default: PrismDemoPage } = await import('./PrismDemoPage');
    createRoot(rootEl).render(
      <React.StrictMode>
        <PrismDemoPage />
      </React.StrictMode>,
    );
    return;
  }

  // /dev/delivery-demo → Sandbox der Lieferungs-Karte (Etappe B), kein Auth, kein
  // API-Call. NUR localhost + staging — auf my.leadscraper.de gibt es den Pfad nicht.
  const DELIVERY_DEMO_HOSTS = new Set(['localhost', '127.0.0.1', 'staging.leadscraper.de']);
  if (path === '/dev/delivery-demo' && DELIVERY_DEMO_HOSTS.has(window.location.hostname)) {
    const rootEl = document.getElementById('root');
    if (!rootEl) throw new Error('#root not found in DOM');
    const { default: DeliveryDemoPage } = await import('./DeliveryDemoPage');
    createRoot(rootEl).render(
      <React.StrictMode>
        <DeliveryDemoPage />
      </React.StrictMode>,
    );
    return;
  }

  // /login, /register, /verify-email, /reset-password always render AuthPage
  if (isAuthPath(path)) {
    // Schalter-Abfrage sofort starten, damit sie parallel zur Sprach-Aufloesung
    // und zum AuthPage-Chunk laeuft (bootAuth wartet auf dasselbe Promise).
    void loadAuthUiConfig();
    // Resolve the auth-page display language WITHOUT persisting it
    // ({ persist: false }) — these are presentation defaults, not user
    // choices, so they must never write ls_lang or PATCH the server. A visitor
    // may still hold a valid session cookie here (me() hasn't run on the auth
    // path), and a persisting auto-default would silently overwrite their saved
    // language and stick app-wide on the next load.
    //   1. An explicit ?lang= (Postmark links) wins over a stale ls_lang.
    //   2. Otherwise, with no explicit signal (no /xx prefix, no ?lang=, no
    //      stored ls_lang, no server user), prefer the browser locale
    //      (navigator.languages) when supported; EN + unmatched browsers fall
    //      back to English (LEA-339), not the global DEFAULT_LANG ('de').
    // A real stored/server preference still wins, and post-login main.tsx
    // re-applies user.language — so a returning German user is never stranded.
    const langParam = readLangParam();
    if (langParam) {
      LangStore.set(langParam, { persist: false });
      document.documentElement.lang = langParam;
    } else if (!hasExplicitLangSignal(firstSeg)) {
      const navLang = navigatorLangParam() ?? 'en';
      LangStore.set(navLang, { persist: false });
      document.documentElement.lang = navLang;
    }
    await bootAuth();
    return;
  }

  // /billing/success and /billing/cancel are post-Stripe-Checkout return pages.
  // They polish the handoff (refresh /api/billing/state, show success/error
  // copy) before redirecting back to the dashboard.
  if (isBillingReturnPath(path)) {
    await bootBillingReturn(path === BILLING_PATHS.success ? 'success' : 'cancel');
    return;
  }

  // Check session with the backend
  let user: PublicUser | null = null;
  try {
    user = await me();
  } catch (err) {
    // Network-/Gateway-Fehler (kein 401 — dafuer gibt me() null zurueck).
    // Nicht in die Sackgasse: Screen zeigen, im Hintergrund weiter versuchen und
    // AN ORT UND STELLE weiterbooten, sobald die API wieder antwortet. Kein
    // Full-Document-Reload, kein erneuter Bundle-Download.
    const msg = err instanceof Error ? err.message : window.t('billing.unknownError');
    const screen = bootReconnect(window.t('boot.connectionErrorDesc', { msg }));
    if (!screen) return;
    let attempt = 0;
    for (;;) {
      attempt += 1;
      screen.render(attempt);
      await screen.waitOrNudge(reconnectDelay(attempt - 1));
      // Im Hintergrund-Tab gar nicht erst anfragen — der visibilitychange-Nudge
      // weckt uns, sobald der Nutzer zurueck ist. Sonst pingen verwaiste Tabs
      // genau den Origin dauerhaft an, auf dessen Erholung wir warten.
      if (document.visibilityState === 'hidden') {
        attempt -= 1; // zaehlt erst, wenn wirklich ein Request rausging
        continue;
      }
      try {
        user = await me();
        break;
      } catch {
        /* weiterhin nicht erreichbar — Screen bleibt stehen, naechster Versuch */
      }
    }
    screen.dispose();
  }

  if (!user) {
    await bootAuth();
    return;
  }

  // Store user globally for prototype .jsx files that read it
  window.__cortexUser = user;

  // LEA-230 (Om 2026-05-03): mirror the server-side language preference into
  // localStorage so future fresh page loads that DON'T call /api/me — most
  // notably the post-Stripe-Checkout return pages /billing/success and
  // /billing/cancel — can pick up the correct language. Without this,
  // BillingReturnPage falls back to German because __cortexUser is null on
  // those routes (bootBillingReturn() is invoked before me() in bootApp).
  //
  // LEA-287 (Om 2026-05-28): the account language is documented as priority #1
  // in LangStore's hydration, but LangStore hydrates at module-import time —
  // before me() has populated window.__cortexUser — so on a fresh load (empty
  // localStorage, no /en URL prefix) it falls through to DE even when the
  // account is EN. Now that we have the user, apply it to the LIVE store on a
  // mismatch: LangStore.set() updates the current language and notifies
  // subscribers, so the first render is already in the right language.
  try {
    if (user.language && isSupportedLang(user.language)) {
      localStorage.setItem('ls_lang', user.language);
      if (LangStore.get() !== user.language) {
        LangStore.set(user.language);
        document.documentElement.lang = user.language;
      }
    }
  } catch {
    // localStorage disabled (Safari private mode etc.) — non-fatal.
  }

  // Crisp support chatbox (Hugo). Loaded HERE — not in index.html — so it boots
  // in the logged-in user's own language: CRISP_RUNTIME_CONFIG.locale has to be
  // set BEFORE l.js loads, and the language is only known after me(). Gated to
  // the real customer origins: localhost (dev), staging.*, and the two prod
  // hosts (my.leadscraper.de + cortex.deimann.io). Both prod origins serve the
  // live SPA and share ONE Crisp website across all envs (confirmed w/ Janik,
  // LEA-307), so the hardcoded CRISP_WEBSITE_ID below is correct everywhere.
  // Never loads on preview/PR hosts. Also identifies the user (email, name) and
  // pushes a `setup_complete` flag (companyWebsite = onboarding step 1) for the
  // "account not set up" trigger.
  try {
    const host = window.location.hostname;
    // LEA-307 prod cutover: Hugo now live on both prod customer origins, not
    // just staging. Additive to the existing localhost + staging.* matching.
    const PROD_HOSTS = ['my.leadscraper.de', 'cortex.deimann.io'];
    if (host === 'localhost' || host.indexOf('staging') !== -1 || PROD_HOSTS.includes(host)) {
      const w = window as unknown as {
        $crisp: unknown[];
        CRISP_WEBSITE_ID: string;
        CRISP_RUNTIME_CONFIG?: { locale?: string };
      };
      w.$crisp = w.$crisp || [];
      w.CRISP_WEBSITE_ID = '17f09dde-09ad-4ac0-9605-3ff7d9992451';
      if (user.language) w.CRISP_RUNTIME_CONFIG = { locale: user.language };
      if (user.email) w.$crisp.push(['set', 'user:email', [user.email]]);
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      if (fullName) w.$crisp.push(['set', 'user:nickname', [fullName]]);
      w.$crisp.push([
        'set',
        'session:data',
        [
          [
            ['language', user.language],
            ['setup_complete', user.companyWebsite ? 'true' : 'false'],
            ['account_status', user.accountStatus],
            ['plan', user.trialPlan ?? ''],
          ],
        ],
      ]);
      // Hand Hugo a short-lived, READ-ONLY, uid-bound support token (cached per
      // tab) so it can load THIS user's context injection-safely — see
      // apps/api/src/routes/support.ts. Non-fatal if it fails.
      try {
        const cached = sessionStorage.getItem('ls_support_token');
        const cachedExp = Number(sessionStorage.getItem('ls_support_token_exp') ?? '0');
        if (cached && cachedExp > Date.now() + 60_000) {
          w.$crisp.push(['set', 'session:data', [[['support_token', cached]]]]);
        } else {
          void fetch('/api/support/token', { method: 'POST', credentials: 'include' })
            .then((r) => (r.ok ? (r.json() as Promise<{ token?: string; expiresAt?: number }>) : null))
            .then((data) => {
              if (data?.token) {
                try {
                  sessionStorage.setItem('ls_support_token', data.token);
                  sessionStorage.setItem('ls_support_token_exp', String(data.expiresAt ?? 0));
                } catch {
                  /* sessionStorage disabled — token still pushed below */
                }
                w.$crisp.push(['set', 'session:data', [[['support_token', data.token]]]]);
              }
            })
            .catch(() => {
              /* token mint failed — Hugo just gets identity without deep context */
            });
        }
      } catch {
        /* sessionStorage disabled — skip token, identity still set */
      }

      if (!document.getElementById('crisp-loader')) {
        const s = document.createElement('script');
        s.id = 'crisp-loader';
        s.src = 'https://client.crisp.chat/l.js';
        s.async = true;
        document.head.appendChild(s);
      }

      // LEA-307 Bug #1: re-localize Hugo when the user switches language in
      // Account settings. CRISP_RUNTIME_CONFIG.locale is only read ONCE at l.js
      // load, so without this the widget stays in the boot-time language until a
      // hard page refresh (closing/reopening the widget doesn't help — same
      // loaded instance). Account.jsx calls LangStore.set() on change; we react
      // to it and push the documented runtime command, which re-localizes the
      // already-open widget without a reload. $crisp is a queue, so this is
      // safe even if a change fires before l.js finishes loading. We also keep
      // the `language` field in session:data in sync so Hugo's own context
      // (which it reads to answer in the user's language) doesn't go stale.
      LangStore.subscribe(() => {
        const lang = LangStore.get();
        w.$crisp.push(['config', 'locale', [lang]]);
        w.$crisp.push(['set', 'session:data', [[['language', lang]]]]);
      });
    }
  } catch {
    // non-fatal — Crisp must never break the app boot.
  }

  // Load the prototype .jsx modules. They register their exports as window
  // globals via Object.assign(window, …); the prototypeShim Proxy resolves
  // cross-file references LAZILY at render time, so load order only matters
  // for the handful of modules that touch another module at EVAL time:
  //   • UIPrimitives.jsx defines `Icon`; Notifications.jsx + AIAssistant.jsx
  //     extend it on load (and bail if it's missing) → must run AFTER it.
  //   • App.jsx calls createRoot().render() on load → must run LAST.
  // Everything else is order-independent. So instead of one ~24-step serial
  // waterfall (each `await` blocked the NEXT import from even starting →​ ~24
  // sequential round-trips on a cold load), we load in three parallel waves.
  const modules = import.meta.glob<unknown>([
    './lib/*.jsx',
    './components/**/*.jsx',
    './features/**/*.jsx',
    './pages/*.jsx',
    './App.jsx',
  ]);

  const loadModule = (key: string): Promise<unknown> => {
    const loader = modules[key];
    if (!loader) {
      console.error(`[boot] missing prototype module: ${key}`);
      return Promise.resolve();
    }
    return loader();
  };

  // Wave 1 — base libs every page/feature reads. UIPrimitives defines `Icon`,
  // which the wave-2 icon-extenders (Notifications, AIAssistant) depend on.
  const BASE_LIBS = [
    './components/animations/Transitions.jsx',
    './lib/api.jsx',
    './lib/data.jsx',
    './components/ui/UIPrimitives.jsx',
    './lib/i18n.jsx',
    './components/layout/NavHeader.jsx',
  ];

  // Wave 2 — pages + features. Order-independent: each self-registers and
  // reads other globals only at render time. Notifications/AIAssistant extend
  // `Icon` at load, but wave 1 has already defined it.
  const FEATURE_MODULES = [
    './pages/Dashboard.jsx',
    './pages/Results.jsx',
    './features/leads/LeadsTable.jsx',
    './pages/LeadDetail.jsx',
    './pages/Memory.jsx',
    './features/memory/MemoryStatus.jsx',
    // W32-Audit: LearnedPatterns.jsx (Stage-Hero/Cluster-Cards der alten
    // Zielgruppen-Seite) wird seit dem Dossier-Umbau (W21) nirgends mehr
    // gemountet — aus dem Bundle genommen statt totes Gewicht zu laden.
    './features/memory/Dossier.jsx',
    './pages/ProfileDetail.jsx',
    './features/integrations/CRMIntegrations.jsx',
    './features/pricing/PricingModal.jsx',
    './features/pricing/OnboardingGate.jsx',
    './features/pricing/ExpiredGate.jsx',
    './features/cancellation/CancellationModal.jsx',
    './pages/Account.jsx',
    // Autopilot-Modus (flag-dark, Port aus feat/autopilot-setup-experience):
    // registriert AutopilotMode + probeAutopilot als Globals — App.jsx probt
    // beim Boot. Jede Flaeche in einer eigenen Datei; alle self-registern und
    // loesen einander erst zur Render-Zeit auf, die Reihenfolge hier ist also
    // nur Lesbarkeit (Basis → Flaechen → Rumpf).
    './features/autopilot/apShared.jsx',
    './features/autopilot/apDemoData.jsx',
    './features/autopilot/ApOverview.jsx',
    './features/autopilot/ApWarmup.jsx',
    './features/autopilot/apLeadStore.jsx',
    './features/autopilot/ApApprovals.jsx',
    './features/autopilot/ApReplies.jsx',
    './features/autopilot/ApResonance.jsx',
    './features/autopilot/ApTech.jsx',
    './features/autopilot/ApHistory.jsx',
    './features/autopilot/AutopilotMode.jsx',
    './features/profile-wizard/NewProfileWizard.jsx',
    './features/search-overlay/SearchOverlay.jsx',
    './features/profile-wizard/FeasibilityModal.jsx',
    './features/notifications/Notifications.jsx',
    './features/scheduling/Scheduling.jsx',
    './features/scheduling/ScheduleCard.jsx',
    './features/scheduling/RunsTimeline.jsx',
    // V1.26.3 (Janik 2026-05-06): MobileApp.jsx loader removed —
    // screen-dashboard.jsx + nav.jsx is now responsive (single source).
    // './features/mobile/MobileApp.jsx',
    './features/ai-assistant/AIAssistant.jsx',
  ];

  // Prism: expose the funnel component as a global so OnboardingGate.jsx (shim)
  // can render it for layout 'prism'. The gate resolves it lazily at render
  // time, so registering it before App mounts is sufficient.
  window.PrismShell = PrismShell;
  // LEA-379 Delivery-UX: Post-Kauf-Fortschrittsbanner, konsumiert von App.jsx
  // über den prototypeShim (gleicher Mechanismus wie PrismShell).
  window.SeedProgressBanner = SeedProgressBanner;
  window.ContactPackRefillCard = ContactPackRefillCard;

  // Wave 3 — App.jsx mounts via createRoot, so it must run only after every
  // global it renders is registered.
  await Promise.all(BASE_LIBS.map(loadModule));
  await Promise.all(FEATURE_MODULES.map(loadModule));
  await loadModule('./App.jsx');
}

void bootApp();
